/**
 * town_ui.ts — the small DOM kit the TOWN LIFE windows share (2026-09-24). THE TOKEN LAW: every colour, face and
 * corner is a `--ax-*` token or a shared class (`ax-btn`, `ax-input`, `ax-seg`, `ax-chip`, `ax-head`, `ax-num`).
 */
import { bus } from '../../bus.js';

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, css = '', text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (css) e.style.cssText = css;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function btn(label: string, onClick: () => void, primary = false, title = ''): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = primary ? 'ax-btn is-primary' : 'ax-btn';
  b.textContent = label;
  if (title) b.title = title;
  b.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
  return b;
}

export function input(placeholder: string, value = '', type = 'text', width = ''): HTMLInputElement {
  const i = document.createElement('input');
  i.className = 'ax-input';
  i.type = type;
  i.placeholder = placeholder;
  i.value = value;
  if (width) i.style.width = width;
  return i;
}

export function select(options: Array<{ value: string; label: string }>, value = ''): HTMLSelectElement {
  const s = document.createElement('select');
  s.className = 'ax-input';
  for (const o of options) { const op = document.createElement('option'); op.value = o.value; op.textContent = o.label; s.appendChild(op); }
  if (value) s.value = value;
  return s;
}

/** A segmented tab strip; `onPick` gets the key. Returns the element and a setter. */
export function tabs(items: Array<{ key: string; label: string }>, current: string, onPick: (k: string) => void): HTMLDivElement {
  const seg = el('div');
  seg.className = 'ax-seg';
  seg.setAttribute('role', 'tablist');
  for (const it of items) {
    const b = document.createElement('button');
    b.textContent = it.label;
    b.dataset.tab = it.key;
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-selected', String(it.key === current));
    if (it.key === current) b.className = 'on';
    b.addEventListener('click', () => onPick(it.key));
    seg.appendChild(b);
  }
  return seg;
}

export function head(text: string): HTMLDivElement {
  const h = el('div', '', text);
  h.className = 'ax-head';
  return h;
}

export function chip(text: string, tone: '' | 'good' | 'bad' | 'warn' | 'info' = ''): HTMLSpanElement {
  const c = el('span', tone ? `color:var(--ax-${tone});border-color:color-mix(in srgb, var(--ax-${tone}) 45%, var(--ax-line2))` : '', text);
  c.className = 'ax-chip';
  return c;
}

export function muted(text: string): HTMLDivElement {
  return el('div', 'color:var(--ax-muted);font-size:12.5px;line-height:1.45', text);
}

/** A labelled row: [label] [control…]. */
export function field(label: string, ...controls: HTMLElement[]): HTMLDivElement {
  const r = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
  r.appendChild(el('span', 'min-width:108px;color:var(--ax-soft)', label));
  for (const c of controls) r.appendChild(c);
  return r;
}

/** A card row in a list. */
export function card(): HTMLDivElement {
  return el('div', 'display:flex;flex-direction:column;gap:6px;padding:8px 10px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg2)');
}

/** Run a reducer call: a success toast, or the server's refusal as a warning toast. */
export async function act(p: Promise<unknown> | undefined, ok: string, after?: () => void): Promise<boolean> {
  if (!p) { bus.emit('atlas:toast', { text: 'Not connected', kind: 'error', ms: 3000 }); return false; }
  try {
    await p;
    if (ok) bus.emit('atlas:toast', { text: ok, kind: 'success', ms: 2600 });
    after?.();
    return true;
  } catch (e) {
    bus.emit('atlas:toast', { text: String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, ''), kind: 'error', ms: 5000 });
    return false;
  }
}

/** A trailing debounce for a row-callback re-render (THE ROW CALLBACK LAW). */
export function debounced(fn: () => void, ms = 150): () => void {
  let t: number | null = null;
  return () => { if (t !== null) return; t = window.setTimeout(() => { t = null; fn(); }, ms); };
}

export const ms = (t: { microsSinceUnixEpoch: bigint } | undefined | null): number => (t ? Number(t.microsSinceUnixEpoch / 1000n) : 0);
